import type { AssembleInput, Grade } from "@repo/hindi-shared";
import {
  assembleSession,
  lessonById,
  lessonList,
  mulberry32,
} from "@repo/hindi-shared";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";

import { createSpeechAudio } from "../audio";
import { dateKey, hashSeed } from "../exercises/seed";
import { navigate } from "../router";
import { takePendingPlan } from "../session/customPlan";
import type { RunnerState } from "../session/reducer";
import { initRunner, runnerReducer, sessionAccuracy } from "../session/reducer";
import { ExerciseHost, PaperBlockHost } from "../session/runner";
import { resolveItemKind } from "../state/itemKind";
import {
  applyGradeSynced,
  completeLessonSynced,
  recordSessionSynced,
} from "../state/mutations";
import { loadState, saveState } from "../state/store";
import {
  getSnapshot as getSyncSnapshot,
  subscribe as subscribeSync,
} from "../state/syncStatus";
import { requestFlush } from "../state/useSync";
import { DualLabel } from "../ui/DualLabel";
import { InkButton } from "../ui/InkButton";
import { ProgressRail } from "../ui/ProgressRail";
import { RubricNumeral } from "../ui/RubricNumeral";
import { Shirorekha } from "../ui/Shirorekha";
import { UNIT_LABELS } from "../unitLabels";

const CHECKPOINT_KEY = "hindi.session.v1";

interface Checkpoint {
  dateKey: string;
  state: RunnerState;
}

function loadCheckpoint(): Checkpoint | null {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(CHECKPOINT_KEY);
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      typeof (parsed as { dateKey?: unknown }).dateKey !== "string" ||
      typeof (parsed as { state?: unknown }).state !== "object"
    ) {
      return null;
    }
    return parsed as Checkpoint;
  } catch {
    return null;
  }
}

function saveCheckpoint(state: RunnerState): void {
  if (typeof window === "undefined") return;
  const checkpoint: Checkpoint = { dateKey: dateKey(), state };
  window.localStorage.setItem(CHECKPOINT_KEY, JSON.stringify(checkpoint));
}

function clearCheckpoint(): void {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(CHECKPOINT_KEY);
}

/**
 * The unit whose last lesson this session just completed, or `null`.
 * Detected from the plan's `newLessonId` (the lesson this session taught)
 * and the lesson→unit map: if every lesson in that unit is now in
 * `completedLessonIds`, finishing this session closed the whole chapter —
 * the summary marks it with the screen's single gold accent.
 */
function completedUnitNumber(
  newLessonId: string | null,
  completedLessonIds: readonly string[],
): number | null {
  if (newLessonId === null) return null;
  const lesson = lessonById.get(newLessonId);
  if (lesson === undefined) return null;
  const completed = new Set(completedLessonIds);
  if (!completed.has(newLessonId)) return null;
  const unitLessons = lessonList.filter((l) => l.unit === lesson.unit);
  return unitLessons.every((l) => completed.has(l.id)) ? lesson.unit : null;
}

/** Total exercise count across every block in a plan (summary block excluded — it holds none). */
function totalExercises(state: RunnerState): number {
  return state.plan.blocks.reduce(
    (sum, block) => sum + block.exercises.length,
    0,
  );
}

function completedExercises(state: RunnerState): number {
  return state.results.length;
}

const audio = createSpeechAudio(() => loadState().settings);

/**
 * SessionScreen — runs one day's assembled practice plan end to end, or a
 * one-off custom plan (drills, single-letter practice from `LetterDetail`)
 * staged via `session/customPlan.ts` before navigating here.
 *
 * The daily plan is built from the persisted store on mount (seed = a hash
 * of `dateKey:sessionCount`, so the plan is stable for the whole calendar
 * day and changes if a second session is started the same day); a custom
 * plan is consumed once from the pending-plan hand-off instead and skips
 * the resume/checkpoint machinery and daily-session bookkeeping entirely —
 * it's practice, not "today's session", so it never increments
 * `sessionCount` or records a session row. Either way, `runnerReducer`
 * drives the learner through each exercise and grades are applied to the
 * store immediately (a drill is still real SRS practice).
 */
export function SessionScreen() {
  // A staged custom plan takes priority and is consumed exactly once, before
  // the daily-session resume check even runs — it never touches the
  // checkpoint, so a custom drill can't collide with an in-progress daily
  // session (or vice versa).
  const customPlan = useMemo(() => takePendingPlan(), []);

  const [resumeChoice, setResumeChoice] = useState<
    "pending" | "resume" | "new"
  >(() =>
    customPlan !== null
      ? "new"
      : loadCheckpoint()?.dateKey === dateKey()
        ? "pending"
        : "new",
  );

  const [runner, setRunner] = useState<RunnerState | null>(() =>
    customPlan !== null ? initRunner(customPlan, Date.now()) : null,
  );

  // `PaperDictation` (unlike every other exercise component) can call its
  // completion callback multiple times against a single mount — once per
  // self-marked item, in one screen — without an intervening remount or
  // render. A plain closure over `runner` state would let two calls that
  // land before React re-renders both read the same stale `runner`,
  // silently dropping one exercise's result and stalling the exerciseIndex
  // advance. This ref always holds the latest runner value synchronously,
  // so `handleComplete` below never operates on stale state even when
  // called several times back to back.
  const runnerRef = useRef(runner);
  runnerRef.current = runner;

  const freshPlanSeed = useMemo(() => {
    const store = loadState();
    return hashSeed(`${dateKey()}:${store.sessionCount}`);
  }, []);

  // Streak from the last bootstrap — rendered ceremonially on the summary.
  // Subscribed here (unconditionally, above the early returns) because
  // hooks can't live inside the `runner.finished` branch below.
  const { streak } = useSyncExternalStore(subscribeSync, getSyncSnapshot);

  useEffect(() => {
    if (customPlan !== null) return; // already initialized in state above
    if (resumeChoice === "pending") return; // waiting on the learner's choice
    if (runner !== null) return; // already started

    if (resumeChoice === "resume") {
      const checkpoint = loadCheckpoint();
      if (checkpoint !== null && checkpoint.dateKey === dateKey()) {
        setRunner(checkpoint.state);
        return;
      }
    }

    const store = loadState();
    const input: AssembleInput = {
      now: Date.now(),
      seed: freshPlanSeed,
      settings: store.settings,
      reviews: Object.values(store.reviews),
      completedLessonIds: store.completedLessonIds,
      sessionCount: store.sessionCount,
    };
    const plan = assembleSession(input);
    const fresh = initRunner(plan, Date.now());
    saveCheckpoint(fresh);
    setRunner(fresh);
  }, [resumeChoice, runner, freshPlanSeed, customPlan]);

  if (resumeChoice === "pending") {
    return (
      <main className="paper-grain flex min-h-dvh flex-col items-center px-6 py-12">
        <section className="w-full max-w-md text-center">
          <Shirorekha>
            <p className="font-latin text-lg text-ink">
              <span lang="en">
                Today&apos;s session isn&apos;t finished. Continue, or start a
                new one?
              </span>
            </p>
            <p lang="hi" className="mt-2 font-dev text-sm text-ink-soft">
              आज का सत्र अधूरा है। जारी रखें या नया शुरू करें?
            </p>
          </Shirorekha>
          <div className="mt-8 flex justify-center gap-4">
            <InkButton
              variant="sindoor"
              onClick={() => setResumeChoice("resume")}
            >
              <DualLabel variant="inline" en="Continue" hi="जारी रखें" />
            </InkButton>
            <InkButton
              variant="ghost"
              onClick={() => {
                clearCheckpoint();
                setResumeChoice("new");
              }}
            >
              <DualLabel variant="inline" en="New session" hi="नया सत्र" />
            </InkButton>
          </div>
        </section>
      </main>
    );
  }

  if (runner === null) {
    return null; // one render tick while the plan assembles
  }

  function handleComplete(result: { grade: Grade; itemId: string }): void {
    // Read/write through the ref, not the `runner` state closure:
    // `PaperDictation` can invoke this once per self-marked item — several
    // calls in a row against the same mount, with no re-render guaranteed
    // between them — and a stale `runner` closure would silently drop
    // results. `runnerRef.current` is updated below before this function
    // returns, so the very next call (even in the same tick) sees the
    // up-to-date state.
    const current = runnerRef.current;
    if (current === null) return;
    const block = current.plan.blocks[current.blockIndex];
    const now = Date.now();

    const store = loadState();
    const rand = mulberry32(hashSeed(`${result.itemId}:${now}`));
    const graded = applyGradeSynced(
      store,
      result.itemId,
      resolveItemKind(result.itemId),
      result.grade,
      now,
      rand,
    );

    const next = runnerReducer(current, {
      type: "complete-exercise",
      itemId: result.itemId,
      grade: result.grade,
      now,
    });

    const isLastInLessonBlock =
      block?.kind === "lesson" &&
      current.exerciseIndex + 1 >= block.exercises.length;
    const withLesson =
      isLastInLessonBlock && block.lessonId !== undefined
        ? completeLessonSynced(graded, block.lessonId, now)
        : graded;

    // A custom (drill) plan is real SRS practice — grades are applied above
    // like any other exercise — but it is not "today's session": it never
    // touches sessionCount, the daily checkpoint, or the session-log/streak
    // bookkeeping that belongs to the once-a-day assembled plan.
    if (customPlan !== null) {
      saveState(withLesson);
      runnerRef.current = next;
      setRunner(next);
      return;
    }

    const finalized = next.finished
      ? { ...withLesson, sessionCount: withLesson.sessionCount + 1 }
      : withLesson;

    saveState(finalized);
    if (next.finished) {
      clearCheckpoint();
      recordSessionSynced({
        id: crypto.randomUUID(),
        dateKey: dateKey(),
        startedAt: current.startedAt,
        durationSec: Math.round((now - current.startedAt) / 1000),
        accuracy: sessionAccuracy(next.results),
      });
      requestFlush();
    } else {
      saveCheckpoint(next);
    }

    runnerRef.current = next;
    setRunner(next);
  }

  const currentBlock = runner.plan.blocks[runner.blockIndex];
  const currentExercise = currentBlock?.exercises[runner.exerciseIndex];

  // Results already recorded for the current "paper" block — populated when
  // resuming a checkpoint saved mid-block (see `handleComplete`'s
  // `saveCheckpoint` call on every non-final self-mark). There is at most
  // one paper block per plan, so matching by `blockKind` alone (rather than
  // also needing a block-instance id) is safe. `PaperBlockHost`/
  // `PaperDictation` use this to resume past already-graded items instead
  // of re-dictating and re-marking the whole block from item 0.
  const alreadyCompletedPaperItems =
    currentBlock?.kind === "paper"
      ? runner.results
          .filter((r) => r.blockKind === "paper")
          .map((r) => ({ itemId: r.itemId, grade: r.grade }))
      : [];

  if (runner.finished) {
    const accuracy = Math.round(sessionAccuracy(runner.results) * 100);
    const store = loadState();
    const finishedUnit =
      customPlan === null
        ? completedUnitNumber(runner.plan.newLessonId, store.completedLessonIds)
        : null;
    const finishedUnitLabel =
      finishedUnit !== null ? UNIT_LABELS[finishedUnit] : undefined;
    return (
      // Top-anchored like Today/Lessons (never viewport-centered): the
      // summary hangs from its shirorekha at the same page rhythm as every
      // other screen instead of floating in the middle of the paper.
      <main className="paper-grain flex min-h-dvh flex-col items-center px-6 py-12">
        <section className="w-full max-w-md text-center">
          <Shirorekha>
            <DualLabel
              en={
                customPlan !== null ? "Practice complete" : "Session complete"
              }
              hi={customPlan !== null ? "अभ्यास पूर्ण" : "सत्र पूर्ण"}
              className="text-xs text-ink-soft uppercase"
            />
            <h1 className="mt-4 font-display text-5xl text-ink">
              <span lang="en">
                {accuracy}
                <span className="text-sindoor">%</span>
              </span>
              <RubricNumeral
                value={accuracy}
                className="ml-2 text-[0.6em] opacity-70"
              />
            </h1>
          </Shirorekha>
          {/* The one gold accent in the app: a whole unit closing is the
              rare illuminated moment — a single gilded line, nothing else. */}
          {finishedUnitLabel !== undefined && (
            <p className="mt-6 font-dev text-lg text-gold">
              ❋ <span lang="en">{finishedUnitLabel.en} — unit complete</span>{" "}
              <span lang="hi" className="text-sm">
                {finishedUnitLabel.hi} — इकाई पूर्ण
              </span>
            </p>
          )}
          <p className="mt-6 font-latin text-base text-ink-soft">
            <span lang="en">{runner.results.length} exercises completed</span>{" "}
            <RubricNumeral
              value={runner.results.length}
              className="text-[0.7em] opacity-70"
            />
          </p>
          {customPlan === null && streak !== null && streak >= 1 && (
            <p className="mt-2 font-latin text-base text-ink-soft">
              <span lang="en">Streak — {streak} days</span>{" "}
              <RubricNumeral
                value={streak}
                className="text-[0.7em] opacity-70"
              />
            </p>
          )}
          {customPlan === null && (
            <p className="mt-2 font-latin text-base text-ink-soft">
              <span lang="en">Sessions so far — {store.sessionCount}</span>{" "}
              <RubricNumeral
                value={store.sessionCount}
                className="text-[0.7em] opacity-70"
              />
            </p>
          )}
          <p className="mt-6 font-latin text-lg text-ink italic">
            <span lang="en">
              {customPlan !== null
                ? "Good practice."
                : "That's enough for today — see you tomorrow."}
            </span>
          </p>
          <div className="mt-10">
            <InkButton
              variant="sindoor"
              onClick={() => navigate(customPlan !== null ? "/drills" : "/")}
            >
              <DualLabel
                variant="inline"
                en={customPlan !== null ? "Back to Drills" : "Home"}
                hi={customPlan !== null ? "अभ्यास पर वापस" : "घर चलें"}
              />
            </InkButton>
          </div>
        </section>
      </main>
    );
  }

  if (currentExercise === undefined) {
    return null;
  }

  return (
    <main className="paper-grain flex min-h-dvh flex-col items-center px-6 py-12">
      {/* The rail is the sheet's shirorekha — the headline rule the whole
          exercise hangs from, filling with ink as the session advances.
          Hidden in print so a paper-dictation reveal prints as a clean
          answer sheet (see the print styles in PaperDictation). */}
      <div className="w-full max-w-md print:hidden">
        <ProgressRail
          total={totalExercises(runner)}
          done={completedExercises(runner)}
        />
      </div>
      {/* Content hangs directly beneath the rail (top-anchored, never
          viewport-centered) so nothing floats in a sea of paper. */}
      <div className="mt-6 w-full max-w-md">
        {currentBlock?.kind === "paper" ? (
          // The "paper" block is one PaperDictation mount for every
          // exercise in the block, not one component per exercise — it
          // self-manages intro/dictation/reveal/self-mark and calls back
          // once per item, driving the same per-item runner advance as
          // every other exercise kind.
          <PaperBlockHost
            runnerKey={`${runner.blockIndex}-paper`}
            exercises={currentBlock.exercises}
            audio={audio}
            onItemComplete={handleComplete}
            alreadyCompleted={alreadyCompletedPaperItems}
          />
        ) : (
          <ExerciseHost
            runnerKey={`${runner.blockIndex}-${runner.exerciseIndex}-${currentExercise.itemId}`}
            exercise={currentExercise}
            audio={audio}
            onComplete={handleComplete}
          />
        )}
      </div>
    </main>
  );
}
